<template>
  <v-form ref="form" v-model="valid" lazy-validation>
    <v-select
      v-model="localValue.policyType"
      :items="policyTypeOptions"
      :disabled="isInEditMode"
      :rules="[required()]"
      :label="$t('devicesSettings.devicePolicyTab.form.policyType')"
      variant="outlined"
      class="mb-6"
      item-title="title"
      item-value="value"
      data-testid="device-policy-type-select"
    />

    <component
      v-if="subFormComponent"
      :is="subFormComponent"
      ref="componentRef"
      v-model:config="localValue"
    />

    <div class="subtitle2 mb-2 mt-6">
      {{ $t("devicesSettings.devicePolicyTab.form.appliedToDevices") }}
    </div>
    <v-autocomplete
      v-model="localValue.labels"
      :items="labels"
      :rules="[atLeastOneIsRequired()]"
      variant="outlined"
      item-title="name"
      return-object
      chips
      multiple
      closable-chips
      clear-on-select
      :placeholder="$t('devicesSettings.devicePolicyTab.form.appliedToDevicesPlaceholder')"
      data-testid="device-policy-labels-select"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :closable="true"
          variant="flat"
          size="default"
          :color="item.raw.color"
          close-icon="$closeCircle"
          :text="item.title"
          label
          rounded="3"
        >
          <span class="coro-label text-uppercase text-primary">{{ item.title }}</span>
        </v-chip>
      </template>
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.name">
          <template v-slot:prepend>
            <coro-label :background-color="item.raw.color" variant="square" class="mr-2" />
          </template>
        </v-list-item>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from "vue";
import cloneDeep from "lodash/cloneDeep";
import { useI18n } from "vue-i18n";
import CoroLabel from "@/components/CoroLabel.vue";
import FileIntegrityPolicyModal from "@/components/modals/device-policy/FileIntegrityPolicyModal.vue";
import { DevicePolicyAction, DevicePolicyType, OsType } from "@/constants/devices";
import { atLeastOneIsRequired, required } from "@/_helpers/validators";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";
import { IntervalUnit, type DevicePolicyFormModel } from "@/constants/device-policy";
import type { DialogDataConfig } from "@/_store/dialogs.module";

interface VuetifyFormRef {
  validate: () => Promise<{ valid: boolean }>;
}

// Form defaults, not a response item: policyType lives on the form model so the type select
// has something to bind to.
export const defaultFileIntegrityPolicyItem: DevicePolicyFormModel = {
  policyType: DevicePolicyType.FILE_INTEGRITY,
  os: OsType.WINDOWS,
  repeatEvery: 1,
  intervalUnit: IntervalUnit.HOURS,
  monitoredPaths: [""],
  labels: [],
};

export const defaultItemForType = (policyType?: DevicePolicyType): DevicePolicyFormModel => {
  switch (policyType) {
    case DevicePolicyType.FILE_INTEGRITY:
      return cloneDeep(defaultFileIntegrityPolicyItem);
    default:
      return { labels: [] }; // type-unselected Add state
  }
};

export default defineComponent({
  components: {
    CoroLabel,
    FileIntegrityPolicyModal,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<DevicePolicyFormModel>>,
      required: true,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const devicesSettingsStore = useDevicesSettingsStore();

    // File integrity is the only policy type shipping in the next version, so a new policy
    // @TODO remove when new policies will be added
    const initialPolicyType = props.config.item?.policyType ?? DevicePolicyType.FILE_INTEGRITY;

    const localValue = ref<DevicePolicyFormModel>({
      ...defaultItemForType(initialPolicyType),
      ...cloneDeep(props.config.item),
    });

    const labels = ref<DeviceLabel[]>([]);
    const form = ref<VuetifyFormRef>();
    const componentRef = ref<{ validate?: () => boolean | Promise<boolean> }>();
    const valid = ref(true);

    const isInEditMode = computed(() => props.config.action === DevicePolicyAction.EDIT);

    const policyTypeOptions = computed(() => [
      {
        value: DevicePolicyType.FILE_INTEGRITY,
        title: i18n.t("devicesSettings.devicePolicyTab.policyTypes.fileIntegrity"),
      },
    ]);

    const subFormComponent = computed(() => {
      switch (localValue.value.policyType) {
        case DevicePolicyType.FILE_INTEGRITY:
          return "FileIntegrityPolicyModal";
        default:
          return null;
      }
    });

    const validate = async () => {
      const result = await form.value?.validate();
      let componentValid = true;
      if (componentRef.value?.validate) {
        componentValid = await componentRef.value.validate();
      }
      const isValid = Boolean(result?.valid) && componentValid;
      emit("update:valid", isValid);
      return isValid;
    };

    watch(
      valid,
      (value) => {
        if (value === null) {
          return;
        }
        emit("update:valid", value);
      },
      { immediate: true }
    );

    watch(
      localValue,
      (value) => {
        emit("update:localValue", value);
      },
      { deep: true, immediate: true }
    );

    onMounted(async () => {
      const { data } = await devicesSettingsStore.searchLabels("");
      labels.value = data;
    });

    return {
      localValue,
      labels,
      form,
      componentRef,
      valid,
      isInEditMode,
      policyTypeOptions,
      subFormComponent,
      required,
      atLeastOneIsRequired,
      validate,
    };
  },
});
</script>
